import utmFieldConfig from '../config/utmFieldConfig.json'

const MUNIM_UTM_COOKIE_NAME = 'munim_utm'
const UTM_TAGS_ROW_ID_COOKIE_NAME = 'utm_tag_record_id'
const GENERATE_LOGIN_OTP_UTM_TAGS_ROW_ID_FIELD = 'utm_tag_record_id'

// check empty string
const isNonEmpty = (value) => {
    if (value == null) {
        return false
    }
    const text = String(value).trim().toLowerCase()
    return text !== '' && text !== 'undefined' && text !== 'null'
}

// normalize search
const normalizeSearch = (search = '') => {
    const raw = String(search || '').trim()
    if (!raw) {
        return ''
    }
    return raw.startsWith('?') ? raw.slice(1) : raw
}
// convert search to search params
const toSearchParams = (search = '') => new URLSearchParams(normalizeSearch(search))

// get url param
const getUrlParam = (searchParams, key) => {
    const value = searchParams.get(key)
    return isNonEmpty(value) ? String(value).trim() : ''
}

// check if url gate is open
const isUrlGateOpen = (searchParams, urlGateParams = []) =>
    urlGateParams.every((key) => isNonEmpty(getUrlParam(searchParams, key)))

/** Root cookie munim_utm → nested UTM key/values (not individual root cookies). */
const parseMunimUtmCookie = (getCookie) => {
    const raw = getCookie ? getCookie(MUNIM_UTM_COOKIE_NAME) : ''
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        return raw
    }
    const text = String(raw || '').trim()
    if (!text) {
        return {}
    }
    try {
        let jsonText = text
        try {
            jsonText = decodeURIComponent(text)
        } catch (_decodeErr) {
            jsonText = text
        }
        const parsed = JSON.parse(jsonText)
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {}
        }
        return parsed
    } catch (_parseErr) {
        return {}
    }
}

const getMunimUtmField = (munimUtm = {}, key) => {
    const value = munimUtm[key]
    return isNonEmpty(value) ? String(value).trim() : ''
}

// resolve utm params with sources
export const resolveUtmParamsWithSources = (search = '', getCookie, config = utmFieldConfig) => {
    const searchParams = toSearchParams(search)
    const bundleFromUrl = isUrlGateOpen(searchParams, config.urlGateParams)
    const munimUtm = bundleFromUrl ? {} : parseMunimUtmCookie(getCookie)
    const utm_params = {}
    const utm_params_from_url = {}
    const utm_params_from_cookie = {}

    ;(config.urlOnlyParams || []).forEach((key) => {
        const urlVal = getUrlParam(searchParams, key)
        if (isNonEmpty(urlVal)) {
            utm_params[key] = urlVal
            utm_params_from_url[key] = urlVal
        }
    })

    ;(config.utmBundleParams || []).forEach((key) => {
        let value = ''
        if (bundleFromUrl) {
            value = getUrlParam(searchParams, key)
            if (isNonEmpty(value)) {
                utm_params_from_url[key] = value
            }
        } else {
            value = getMunimUtmField(munimUtm, key)
            if (isNonEmpty(value)) {
                utm_params_from_cookie[key] = value
            }
        }
        if (isNonEmpty(value)) {
            utm_params[key] = value
        }
    })

    if (!bundleFromUrl) {
        ;(config.cookieOnlyParams || []).forEach((key) => {
            const extra = getMunimUtmField(munimUtm, key)
            if (isNonEmpty(extra)) {
                utm_params_from_cookie[key] = extra
            }
        })
    }

    return {
        utm_params,
        utm_params_from_url,
        utm_params_from_cookie,
        bundle_from_url: bundleFromUrl
    }
}

const pickNonEmptyObject = (source = {}) => {
    const picked = {}
    Object.keys(source || {}).forEach((key) => {
        const value = source[key]
        if (isNonEmpty(value)) {
            picked[key] = String(value).trim()
        }
    })
    return picked
}

/**
 * URL bag vs cookie bag only — never the same key in both.
 * @param {object} marketingCookies - e.g. getUtmCookiesPayload() (_ga, _fbp, …)
 */
export const collectUtmPayload = (search = '', getCookie, marketingCookies = {}, config = utmFieldConfig) => {
    const {
        utm_params,
        utm_params_from_url,
        utm_params_from_cookie,
        bundle_from_url
    } = resolveUtmParamsWithSources(search, getCookie, config)

    const fromUrl = pickNonEmptyObject(utm_params_from_url)
    const fromCookie = pickNonEmptyObject(utm_params_from_cookie)
    if (!bundle_from_url) {
        const marketing = pickNonEmptyObject(marketingCookies)
        Object.keys(marketing).forEach((key) => {
            if (fromUrl[key] == null && fromCookie[key] == null) {
                fromCookie[key] = marketing[key]
            }
        })
    }

    const product_referrer = typeof document !== 'undefined' ? (document.referrer || '').trim() : ''
    const product = getUrlParam(toSearchParams(search), 'product')

    return {
        utm_params,
        utm_params_from_url: fromUrl,
        utm_params_from_cookie: fromCookie,
        product_referrer,
        product
    }
}

// check if utm params are tracked
export const hasTrackedUtmParams = (utm_params = {}) =>
    Object.keys(utm_params).some((key) => isNonEmpty(utm_params[key]))

/** `product` query → CRM source string when `utm_source` is absent. */
export const getProductSourceFallback = (search = '') => {
    const product = getUrlParam(toSearchParams(search), 'product').toLowerCase()
    if (product === 'gst') {
        return 'munim gst'
    }
    if (product === 'app') {
        return 'accounting'
    }
    return ''
}

/**
 * CRM API `source`: resolved utm_source (URL + bundle cookies), then product on current URL, then fallback.
 * Use window.location.search only — not utm_tag (utm_source may be cookie-only).
 */
export const getUtmTagsRowIdCookieName = () => UTM_TAGS_ROW_ID_COOKIE_NAME


export const getGenerateLoginOtpUtmTagsRowIdField = () => GENERATE_LOGIN_OTP_UTM_TAGS_ROW_ID_FIELD


export const readUtmTagsRowIdFromCookies = (getCookie) => {
    const primaryVal = String(getCookie(getUtmTagsRowIdCookieName()) || '').trim()
    return isNonEmpty(primaryVal) ? primaryVal : ''
}

export const getSignupLeadSource = (search = '', getCookie = () => '', fallback = '') => {
    const { utm_params } = resolveUtmParamsWithSources(search, getCookie)
    const fromResolved = utm_params?.utm_source
    if (isNonEmpty(fromResolved)) {
        return String(fromResolved).trim()
    }
    const fromProduct = getProductSourceFallback(search)
    if (isNonEmpty(fromProduct)) {
        return fromProduct
    }
    const resolvedFallback = isNonEmpty(fallback) ? String(fallback).trim() : 'accounting'
    return resolvedFallback
}

export { utmFieldConfig }
